import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle,
  Briefcase,
  CalendarClock,
  Globe,
  Loader2,
  Save,
  Users,
  Wrench,
} from 'lucide-react'
import { DateRangePicker } from './ui/DateRangePicker'
import { SettingsUpdatedAt } from './SettingsUpdatedAt'
import { api, extractApiError } from '../lib/api'
import { getDisplayTimeZone } from '../lib/format'
import { DateTime } from 'luxon'

/** UI metadata per scope — order here is the order rendered in the
 *  section. Keep aligned with `MAINTENANCE_SCOPES` on the backend. */
const SCOPE_META = [
  {
    key: 'website',
    title: 'Website',
    icon: Globe,
    description:
      'Public marketing pages plus the sign-in / sign-up flow. Existing signed-in sessions keep working until the matching panel below is also disabled.',
  },
  {
    key: 'company',
    title: 'Company Admin Panel',
    icon: Briefcase,
    description:
      'The Company Admin and Sub Admin shell at /dashboard — Workspaces, Members, Departments, Reports, and every admin tool.',
  },
  {
    key: 'user',
    title: 'User Panel',
    icon: Users,
    description:
      'The User shell at /app. One switch covers every User-panel login regardless of Work Role — flipping it locks all User-panel sign-ins out at once. Company Admins and Sub Admins are governed by the Company Admin Panel switch above.',
  },
]

function defaultDraft() {
  return Object.fromEntries(
    SCOPE_META.map((s) => [
      s.key,
      { enabled: false, message: '', scheduledStart: null, scheduledEnd: null },
    ]),
  )
}

/** Convert an ISO datetime string to the `yyyy-MM-ddTHH:mm` shape an
 *  `<input type="datetime-local">` expects. Renders the wall-clock in the
 *  person's display timezone (their own zone, else the company's, else
 *  the browser's) so it matches every other time they see. Returns "" for
 *  nullish / unparseable input so React doesn't warn about a controlled
 *  input switching from undefined to a string. */
function isoToLocalInput(iso) {
  if (!iso) return ''
  const dt = DateTime.fromISO(String(iso)).setZone(getDisplayTimeZone() || 'local')
  if (!dt.isValid) return ''
  return dt.toFormat("yyyy-MM-dd'T'HH:mm")
}

/** Inverse of `isoToLocalInput`: reads the wall-clock in the person's
 *  display timezone and returns the UTC ISO instant. Empty string → null
 *  so the backend DTO's `optionalIsoDateTime` happy-paths the "cleared
 *  the field" case without a validation error. */
function localInputToIso(value) {
  if (!value) return null
  const dt = DateTime.fromISO(value, { zone: getDisplayTimeZone() || 'local' })
  if (!dt.isValid) return null
  return dt.toUTC().toISO()
}

/** Is this scope down RIGHT NOW purely because of an active scheduled
 *  window (not the manual toggle)? Mirrors the backend `isScopeInMaintenance`
 *  so the "Down · scheduled" pill matches what the gate actually does — and
 *  a leftover schedule can never silently hold a panel down. */
function isScheduledDownNow(s) {
  if (!s?.scheduledStart || !s?.scheduledEnd) return false
  const start = Date.parse(s.scheduledStart)
  const end = Date.parse(s.scheduledEnd)
  return (
    Number.isFinite(start) &&
    Number.isFinite(end) &&
    Date.now() >= start &&
    Date.now() <= end
  )
}

/** Toggle switch — same shape as the Email Templates / Payments pages
 *  so this surface feels consistent with the rest of the Super Admin
 *  area. Lifted in-file because the project doesn't have a shared
 *  Toggle primitive yet. */
function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ' +
        (checked
          ? 'bg-[color:var(--color-primary)]'
          : 'bg-[color:var(--color-rule-strong)]')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
          (checked ? 'translate-x-4' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

/**
 * Maintenance editor — embedded as a section of the Settings page.
 * Per-panel maintenance toggles (Website / Company Admin / User) with
 * an optional scheduled window each. Reads + writes the cross-tenant
 * `maintenance` setting via `/super-admin/maintenance`; owns its own
 * Save button because its payload + validation are distinct from the
 * key/value settings above it.
 */
export function MaintenanceSettings() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState(defaultDraft)
  // Snapshot of the server state we last synced with — the Save button
  // diffs against this so it only enables when something actually
  // changed. Re-syncs whenever the query refetches.
  const [serverSnapshot, setServerSnapshot] = useState(defaultDraft)

  const statusQuery = useQuery({
    queryKey: ['platform-maintenance'],
    queryFn: async () => {
      const res = await api.get('/super-admin/maintenance')
      return res.data
    },
  })

  useEffect(() => {
    const data = statusQuery.data?.data
    if (!data) return
    const next = defaultDraft()
    for (const { key } of SCOPE_META) {
      const v = data[key]
      if (v) {
        next[key] = {
          enabled: Boolean(v.enabled),
          message: typeof v.message === 'string' ? v.message : '',
          scheduledStart:
            typeof v.scheduledStart === 'string' ? v.scheduledStart : null,
          scheduledEnd:
            typeof v.scheduledEnd === 'string' ? v.scheduledEnd : null,
        }
      }
    }
    setDraft(next)
    setServerSnapshot(next)
  }, [statusQuery.data])

  const isDirty = useMemo(() => {
    return SCOPE_META.some(({ key }) => {
      const a = draft[key]
      const b = serverSnapshot[key]
      return (
        a.enabled !== b.enabled ||
        a.message !== b.message ||
        a.scheduledStart !== b.scheduledStart ||
        a.scheduledEnd !== b.scheduledEnd
      )
    })
  }, [draft, serverSnapshot])

  // Per-scope validation for the schedule pickers. Surfaced inline
  // under the End field and used to block Save so we don't ship a
  // bad payload — the backend rejects it too, but catching client-side
  // saves a round-trip and keeps the error close to the offending
  // input. Two failure modes:
  //   - Only one of (start, end) filled — both are required as a pair.
  //   - End <= Start — pickers don't naturally enforce ordering.
  const scheduleErrors = useMemo(() => {
    const errs = {}
    for (const { key } of SCOPE_META) {
      const { scheduledStart, scheduledEnd } = draft[key]
      if (scheduledStart && !scheduledEnd) {
        errs[key] = 'Set an end time as well.'
      } else if (!scheduledStart && scheduledEnd) {
        errs[key] = 'Set a start time as well.'
      } else if (
        scheduledStart &&
        scheduledEnd &&
        Date.parse(scheduledEnd) <= Date.parse(scheduledStart)
      ) {
        errs[key] = 'End must be after start.'
      }
    }
    return errs
  }, [draft])

  // Only an INVERTED range (end on/before start) truly can't be saved and
  // blocks the button. A half-set schedule (just one of start/end) does NOT
  // block — it's normalised to "no schedule" on save (see the mutation), so
  // clearing a single date is enough and the admin is never stuck.
  const blockingScheduleScopes = useMemo(
    () =>
      SCOPE_META.filter(({ key }) => {
        const { scheduledStart, scheduledEnd } = draft[key]
        return (
          scheduledStart &&
          scheduledEnd &&
          Date.parse(scheduledEnd) <= Date.parse(scheduledStart)
        )
      }).map(({ title }) => title),
    [draft],
  )
  const hasBlockingScheduleError = blockingScheduleScopes.length > 0

  const anyEnabled = SCOPE_META.some(({ key }) => draft[key].enabled)
  const anyScheduled = SCOPE_META.some(({ key }) => {
    const s = draft[key].scheduledStart
    return s && Date.parse(s) > new Date().getTime()
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Normalise a half-set schedule (only one of start/end) to "no
      // schedule" — a window needs both ends, and this stops a partially
      // cleared schedule from sticking or blocking the next save.
      const payload = {}
      for (const { key } of SCOPE_META) {
        const s = draft[key]
        const paired = Boolean(s.scheduledStart && s.scheduledEnd)
        payload[key] = {
          enabled: s.enabled,
          message: s.message,
          scheduledStart: paired ? s.scheduledStart : null,
          scheduledEnd: paired ? s.scheduledEnd : null,
        }
      }
      const res = await api.put('/super-admin/maintenance', payload)
      return res.data
    },
    onSuccess: () => {
      toast.success('Maintenance settings saved successfully.')
      void qc.invalidateQueries({ queryKey: ['platform-maintenance'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function patchScope(key, patch) {
    setDraft((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }))
  }

  return (
    <section>
      {/* Section header mirrors the page header above (title left, a
       *  scoped Save button right). The button saves only the
       *  maintenance payload — the key/value settings have their own
       *  "Save all changes" button at the top of the page. */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5 m-0">
            <Wrench
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            Maintenance
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Turn parts of the app off for maintenance.
          </p>
        </div>
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={!isDirty || saveMutation.isPending || hasBlockingScheduleError}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save size={14} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {hasBlockingScheduleError ? (
        // Surface WHY Save is greyed out — otherwise an inverted schedule
        // window silently disables the button with only a hint under the
        // (possibly scrolled-off) date picker.
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] px-3 py-2 text-sm text-[color:var(--color-warning)]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Save is disabled because a schedule&rsquo;s end time is on or before
            its start time ({blockingScheduleScopes.join(', ')}). Fix the end
            time, or clear both dates to remove the schedule.
          </span>
        </div>
      ) : null}

      {anyEnabled ? (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] px-3 py-2 text-sm text-[color:var(--color-warning)]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            At least one panel is set to maintenance. Users in that panel
            will be locked out the moment you press Save. You won&rsquo;t be
            locked out yourself &mdash; Master Admin accounts are exempt &mdash;
            so test the lockout with a Company Admin or User account, not this
            session.
          </span>
        </div>
      ) : null}

      {anyScheduled ? (
        // A scheduled window now auto-activates + lifts on its own (the
        // gate treats "now inside [start, end]" as down). Saving also fans
        // out the heads-up email to affected users.
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 text-sm text-[color:var(--color-ink-muted)]">
          <CalendarClock
            size={14}
            className="mt-0.5 shrink-0 text-[color:var(--color-accent-strong)]"
          />
          <span>
            One or more panels have a scheduled window. Each panel goes down
            automatically at its start time and comes back at its end time —
            and saving emails affected users so they know it&rsquo;s coming.
          </span>
        </div>
      ) : null}

      {statusQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
      ) : statusQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(statusQuery.error).message}
        </p>
      ) : (
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          {SCOPE_META.map(({ key, title, icon: Icon, description }, idx) => {
            const state = draft[key]
            const isLast = idx === SCOPE_META.length - 1
            return (
              <div
                key={key}
                className={
                  'px-6 py-5 ' +
                  (isLast ? '' : 'border-b border-[color:var(--color-rule)]')
                }
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-2 m-0">
                      <Icon
                        size={20}
                        className="text-[color:var(--color-accent-strong)]"
                      />
                      {title}
                    </h3>
                    <p className="mt-1 text-sm text-[color:var(--color-ink-muted)] leading-relaxed">
                      {description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(() => {
                      // Effective state: the manual toggle OR an active
                      // scheduled window. A panel held down by a schedule
                      // shows "Down · scheduled" even though the toggle reads
                      // off — so it's never a mystery why the site is down.
                      const scheduledDown =
                        !state.enabled && isScheduledDownNow(state)
                      if (state.enabled || scheduledDown) {
                        return (
                          <span className="text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]">
                            {scheduledDown ? 'Down · scheduled' : 'Down'}
                          </span>
                        )
                      }
                      return (
                        <span className="text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
                          Live
                        </span>
                      )
                    })()}
                    <Toggle
                      checked={state.enabled}
                      onChange={(next) => patchScope(key, { enabled: next })}
                      label={`Toggle maintenance for ${title}`}
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <DateRangePicker
                    value={{
                      start: isoToLocalInput(state.scheduledStart),
                      end: isoToLocalInput(state.scheduledEnd),
                    }}
                    onChange={(next) =>
                      patchScope(key, {
                        scheduledStart: localInputToIso(next.start),
                        scheduledEnd: localInputToIso(next.end),
                      })
                    }
                    error={scheduleErrors[key]}
                  />
                </div>

                {state.scheduledStart &&
                state.scheduledEnd &&
                !scheduleErrors[key] ? (
                  <p className="mt-2 text-xs text-[color:var(--color-ink-muted)] flex items-center gap-1.5">
                    <CalendarClock size={12} />
                    Goes down automatically at the start, back up at the end.
                    Affected users are emailed on Save.
                  </p>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
      <SettingsUpdatedAt at={statusQuery.data?.updatedAt} />
    </section>
  )
}
